// import AIChatButton as A from "./ai/components/AIChatButton";

import React, { lazy, Suspense } from "react";
import AlertModal from "../components/AlertModal/AlertModal";
import useConfig from "../demos/hooks/useConfig";

// Error Boundary component for AIChatButton
class AIChatErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; showModal: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, showModal: false };
  }

  static getDerivedStateFromError(error: Error): {
    hasError: boolean;
    showModal: boolean;
  } {
    // The label appears; the dialog does not. A site whose owner has not
    // chosen an agent yet is not in an error state — it is halfway through
    // being set up, and greeting its first visitor with a licensing dialog
    // they cannot act on, over a choice that was deliberately postponed, was
    // the worst possible reading of it. The label says what is going on, and
    // opens the details for whoever actually wants them.
    return { hasError: true, showModal: false };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log error to console or error reporting service
    console.error("AIChatButton error:", error, errorInfo);
  }

  handleCloseModal = () => {
    this.setState({ showModal: false });
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <>
          <div
            role="button"
            tabIndex={0}
            onClick={() => this.setState({ showModal: true })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") this.setState({ showModal: true });
            }}
            style={{
              color: "var(--fg-muted, #6b7280)",
              fontSize: "12px",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            title="What this means"
          >
            AI Chat not configured
          </div>
          <AlertModal
            isOpen={this.state.showModal}
            onClose={this.handleCloseModal}
            title="AI Chat not configured"
            description={
              <>
                No AI assistant is connected to this site yet. If you expected
                one, connect an agent to it in CADViewer Analytic — or ask about
                the AI module at{" "}
                <a
                  href="mailto:internationalsales@tailormade.com"
                  className="text-primary-600 hover:text-primary-800 underline"
                >
                  internationalsales@tailormade.com
                </a>
              </>
            }
            closeText="Close"
          />
        </>
      );
    }

    return this.props.children;
  }
}

// Export the AIChatButton component if config.enableAIChat is true or return dummy component
export const AIChatButton = () => {
  const { config } = useConfig();
  if (config.enableAIChat) {
    // lazy loading
    // const importAIChatPath = ;
    // @ts-ignore - Module may or may not exist depending on AI license availability
    const AIChatButton = lazy(
      // @ts-ignore - Dynamic import path that may not exist
      () => import("./ai/components/AIChatButton/AIChatButton")
    );
    return (
      <AIChatErrorBoundary>
        <Suspense fallback={<div>Loading AI Chat...</div>}>
          <AIChatButton />
        </Suspense>
      </AIChatErrorBoundary>
    );
  }
  return <div />;
};
